import { Button } from '@pms-digital/design-system';
import { type ReactNode, useId, useState } from 'react';

/**
 * The fold of a block of analysis of the parcours (refonte-modulaire.md §12.2,
 * « Depuis #219 et #220 ; » noyau.md §9.1 and §9.4) - one pattern for the three blocks that
 * carry a reasoning: the determination of the noyau, the rangement of the matter, and the
 * two dry trials that draw the same blocks.
 *
 * ## Folded by default, on a line that says the outcome
 *
 * A block of analysis is longer than the page it follows, and the fil of the pages is what
 * somebody opening a parcours reads: the blocks arrive FOLDED, on a line of summary - the
 * outcome, the model, the time, the cost - and a button that unfolds what they always drew
 * (« Voir les deux passes ▾ », « Voir la matière ▾ », « Replier ▴ »). The state is the
 * component's own, like the sections of `walk-noyau-analysis.tsx`: a posture of the reading,
 * never the URL's.
 *
 * What asks for attention stays legible folded: the badges of the header - the state, `style
 * absent`, `lecture directe, avant #196` - are not part of what folds, and a block with no
 * notes to show says its existing sentence in the line of summary.
 *
 * ## The body is unmounted, not hidden
 *
 * A folded body is not drawn at all: the queries it mounts (the entries served) and the
 * marks the e2e suite reads are those of an UNFOLDED block, and a hidden copy would answer
 * both.
 *
 * ## The pill of the head unfolds the block it leads to (#218)
 *
 * `unfold` is the ring the route lends the block (`flash`): when it RISES - or is already up
 * on the render that mounts the block, as when the pill was clicked from the tab Variables -
 * the block opens, so that the scroll that follows lands on what the pill promised. It is
 * adjusted while rendering, not in an effect: the open block is then in the very commit the
 * reading scrolls on. The ring dropped afterwards leaves the block open; the operator folds it.
 */

export interface FoldState {
  readonly open: boolean;
  /** The id of the body, for `aria-controls` - only meaningful while the body is drawn. */
  readonly bodyId: string;
  readonly toggle: () => void;
}

export function useFold(unfold: boolean): FoldState {
  const [open, setOpen] = useState(unfold);
  const [wasUp, setWasUp] = useState(unfold);
  if (unfold !== wasUp) {
    setWasUp(unfold);
    if (unfold) setOpen(true);
  }
  const bodyId = useId();
  return { open, bodyId, toggle: () => setOpen((current) => !current) };
}

export interface FoldToggleProps {
  readonly fold: FoldState;
  /** What the button says while folded, « ▾ » included: « Voir les deux passes ▾ ». */
  readonly closedLabel: string;
  readonly testId: string;
}

/** The button of the header: `aria-expanded`, « Replier ▴ » once open. */
export function FoldToggle({ fold, closedLabel, testId }: FoldToggleProps) {
  return (
    <Button
      type="button"
      variant="accent"
      size="sm"
      data-testid={testId}
      aria-expanded={fold.open}
      aria-controls={fold.open ? fold.bodyId : undefined}
      onClick={fold.toggle}
    >
      {fold.open ? 'Replier ▴' : closedLabel}
    </Button>
  );
}

export interface FoldSummaryProps {
  readonly testId: string;
  readonly children: ReactNode;
}

/** The line a folded block says in place of its body: spans, wrapped and spaced as the facts are. */
export function FoldSummary({ testId, children }: FoldSummaryProps) {
  return (
    <p
      data-testid={testId}
      className="flex flex-wrap items-baseline gap-x-5 gap-y-1 px-5 py-3 text-gray-700 text-small"
    >
      {children}
    </p>
  );
}
